import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthedRequestFn, color, MiscUtils, FormatUtils, safeBigNum } from '@my/util'
import {
  SimpleText,
  XStack,
  YStack,
  FinancialTable,
  ActionButton,
  CustomDropdown,
  useWindowDimensions,
  Card,
  Button,
  H2,
  Paragraph,
  Image,
  View,
  Text,
  H5,
  Separator,
  SizableText,
  Tabs,
  Spinner,
  TZASummaryCards,
  TZATabs,
  ScrollView
} from '@my/ui'
import { BigNumber } from 'bignumber.js'
import { SpendingChart } from './spendingChart'
import { FinancialReviewHeading, ToggleButtonGroup } from '../../../components/index'
import { ExpenseCategoryList } from './expenseCategoryList'
import { BudgetSummary } from './budgetSummaryCard'
import images from '../../../assets/images'
import { ChevronDown, DollarSign, MoveRight, TrendingUp } from '@tamagui/lucide-icons-2'
import { defaultTo } from 'lodash'
import { Platform } from 'react-native'

// Default Table Headings
const DEFAULT_TABLE_HEADINGS = [
  { key: 'expander', text: '' },
  { key: 'category', text: 'Budget Priorities' },
  { key: 'percentageOfSalary', text: '% of Monthly Income' },
  { key: 'amountSpent', text: 'Amount Spent' },
  { key: 'target', text: 'Target %' },
  { key: 'targetAmount', text: 'Target Amount' },
  { key: 'difference', text: 'Difference' },
]

const BudgetCard = ({icon, title, value, subTitle, borderColor, ...rest}) => {
  const backgroundImage = defaultTo(rest.backgroundImage, 'linear-gradient(to right, rgb(250, 250, 250), rgb(232, 232, 232))');
  return (
    <Card elevate size="$4" bordered borderRadius={9} style={{borderColor}} {...rest}>
      <Card.Header padded>
        <XStack>
          <YStack>
            <Paragraph>{title}</Paragraph>
            <H2>{value}</H2>
            {subTitle ? <Paragraph theme="alt2">{subTitle}</Paragraph> : null}
          </YStack>
          {icon ? <View style={{marginLeft: 'auto', borderRadius: 9999, backgroundColor: 'white', height: 32, width: 32, justifyContent: 'center', alignItems: 'center'}}>
            {icon}
          </View> : null}
        </XStack>
      </Card.Header>
      {/* <Card.Footer padded>
        <XStack flex={1} />
        <Button borderRadius="$10">Purchase</Button>
      </Card.Footer> */}
      <Card.Background borderRadius={9} backgroundImage={backgroundImage} />

    </Card>
  )
}

const TabsContent = (props: TabsContentProps) => {
  return (
    <Tabs.Content
      backgroundColor="white"
      key="tab3"
      padding="$2"
      /* alignItems="center"
      justifyContent="center" */
      //flex={1}
      //borderColor="$white"
      borderRadius="$2"
      borderTopLeftRadius={0}
      borderTopRightRadius={0}
      //borderWidth="$2"
      {...props}
    >
      {props.children}
    </Tabs.Content>
  )
}

const calcPercentage = (part: BigNumber, whole: BigNumber): string =>
    String(safeBigNum(part.dividedBy(whole)).multipliedBy(100).dp(2));

const parseCosts = (expenseArray: Array<string>, monthlyExpenses: Record<string, string>, monthlyIncome: string): any[] => {
    const totalIncome = safeBigNum(monthlyIncome);

    const costs = expenseArray.reduce(
        (acc, key: string) => {
            const expense = safeBigNum(monthlyExpenses[key] as any);
            const percentage = calcPercentage(expense, totalIncome);

            acc.arr.push({
                category: key.split('- ')[2],
                amountSpent: monthlyExpenses[key],
                '% of Monthly Salary': percentage,
            });
            acc.total = acc.total.plus(expense);
            return acc;
        },
        { arr: [] as any, total: new BigNumber(0) }
    );

    costs.arr.push({
        category: 'total',
        amountSpent: costs.total.toFixed(2),
        '% of Monthly Salary': calcPercentage(costs.total, totalIncome),
    });

    return costs.arr;
};

export const FinancialSpending = () => {
  const [activePeriod, setActivePeriod] = useState<'1 Month' | '3 Months' | '6 Months'>('3 Months')
  const [filter, setFilter] = useState({
    cost: 'All',
    priorities: '',
  })

  const authedRequestFn = useAuthedRequestFn()
  /* const { data: spendingAnalysisData, isLoading: spendingIsLoading } = useQuery({
    queryKey: ['financial-spending'],
    queryFn: () => authedRequestFn('/plaid/monthly-expenses'),
  }) */
  const { data: budget, isLoading: budgetIsLoading } = useQuery({
    queryKey: ['financial-review'],
    queryFn: () => authedRequestFn('/financial-review'),
  })

  const financialProfileQuery = useQuery({
    queryKey: ['financial-profile'],
    queryFn: () => authedRequestFn('/financial-profile'),
  });

  const financialProfileDataObj = financialProfileQuery?.data;

  const needsExpenses = Object.entries(financialProfileDataObj?.['Expenses - Categorization Mapping'] || {})
            .filter((xp) => xp[1] === 'Need')
            .map((xp) => xp[0]);
  const wantsExpenses = Object.entries(financialProfileDataObj?.['Expenses - Categorization Mapping'] || {})
            .filter((xp) => xp[1] === 'Want')
            .map((xp) => xp[0]);

  // Handle No Data Available
  if (/* spendingAnalysisData?.length == 0 || */ budget?.length == 0) {
    return <SimpleText>No data Available</SimpleText>
  }

  const costFilter = (costObj: any) => costObj.category !== 'total' && !['0', '0.0', '0.00', undefined, null].includes(costObj.amountSpent);
  const variableCostData: Array<any> = budget?.budgetSpendingAnalysis?.variableCosts?.filter(costFilter) || []
  const fixedCostData: Array<any> = budget?.budgetSpendingAnalysis?.fixedCosts?.filter(costFilter) || []
  const needCostData: Array<any> = budget?.budgetSpendingAnalysis?.needCosts?.filter(costFilter) || []
  const wantCostData: Array<any> = budget?.budgetSpendingAnalysis?.wantCosts?.filter(costFilter) || []

  const allCosts = [...variableCostData, ...fixedCostData];

  const needsCosts = allCosts.filter((item) => needsExpenses.includes(item.category));
  const needCostsMap: Record<string, any> = {};
  Object.assign(needCostsMap, ...([...needCostData, ...needsCosts]).map((item) => ({[item.category]: item})));
  const loadedNeeds = Object.keys(needCostsMap);
  const missingNeeds = needsExpenses.filter((expenseName) => !loadedNeeds.includes(expenseName));

  const wantsCosts = allCosts.filter((item) => wantsExpenses.includes(item.category));
  const wantCostsMap: Record<string, any> = {};
  Object.assign(wantCostsMap, ...([...wantCostData, ...wantsCosts]).map((item) => ({[item.category]: item})));
  const loadedWants = Object.keys(needCostsMap);
  const missingWants = wantsExpenses.filter((expenseName) => !loadedWants.includes(expenseName));

  const monthlyIncome = budget?.monthlyCashFlows?.Operating.Income.total || 0;
  const monthlyExpenses = budget?.monthlyCashFlows?.Operating.Expenses.values || {};

  const expenseNameBuilder = (expenseName: string) => `Expenses - Monthly - ${expenseName}`;
  const needCosts = parseCosts(missingNeeds.map(expenseNameBuilder), monthlyExpenses, monthlyIncome);
  const wantCosts = parseCosts(missingWants.map(expenseNameBuilder), monthlyExpenses, monthlyIncome);

  const finalNeedData = [...Object.values(needCostsMap), ...needCosts].filter(costFilter);
  const finalWantData = [...Object.values(wantCostsMap), ...wantCosts].filter(costFilter);


  const budgetBreakdownData = formatBudgetBreakdown(
    budget?.budgetSpendingAnalysis?.budgetBreakdown || {},
    finalNeedData,
    finalWantData
  );
  
  const variableCostTotal = budget?.budgetSpendingAnalysis?.variableCosts?.find((vc) => vc.category === 'total');
  // Helper for variable cost summary
  const variableCostSummary = [
    { title: 'Monthly Amount Spent', value: variableCostTotal ? FormatUtils.currency.cashFormat(variableCostTotal?.amountSpent) : '--' },
    { title: '% of Monthly Income', value: variableCostTotal ? variableCostTotal?.["% of Monthly Salary"]+'%' : '--' },
  ]
  
  const fixedCostTotal = budget?.budgetSpendingAnalysis?.fixedCosts?.find((fc) => fc.category === 'total');
  // Helper for fixed cost summary (Can be extended similarly to variable cost)
  const fixedCostSummary = [
    { title: 'Monthly Amount Spent', value: fixedCostTotal ? FormatUtils.currency.cashFormat(fixedCostTotal?.amountSpent) : '--' },
    { title: '% of Monthly Income', value: fixedCostTotal ? fixedCostTotal?.["% of Monthly Salary"]+'%' : '--' },
  ]

  const body = (/* spendingIsLoading || */ budgetIsLoading || financialProfileQuery.isLoading)
  ? <View style={{justifyContent: 'center', alignItems: 'center', height: '100%'}}><Spinner size="large" color={color.tzaGreen} /></View>
  : <>
      {/* <YStack gap={10}>
        <BudgetCard title="Monthly Spending" value={budget?.financialSnapshotUI?.SpendingPieChart?.Spending?.fmt} subTitle={null} backgroundColor={'#d1fae5'} backgroundImage={'linear-gradient(to right, rgb(236, 253, 245), rgb(209, 250, 229))'} borderColor={'rgb(167, 243, 208)'} icon={<DollarSign color='rgb(16, 185, 129)'/>} />
        <BudgetCard title="Variable Costs" value={variableCostSummary[0].value} backgroundImage={'linear-gradient(to right, rgb(239, 246, 255), rgb(219, 234, 254))'} borderColor={'rgb(191, 219, 254)'} subTitle={variableCostSummary[1].title + ': '+ variableCostSummary[1].value} icon={<TrendingUp color='rgb(59, 130, 246)' />} />
        <BudgetCard title="Fixed Costs" value={fixedCostSummary[0].value} backgroundImage={'linear-gradient(to right, rgb(250, 245, 255), rgb(243, 232, 255))'} borderColor={'rgb(233, 213, 255)'} subTitle={fixedCostSummary[1].title + ': '+ fixedCostSummary[1].value} icon={<MoveRight color='rgb(168, 85, 247)' />} />
        <BudgetCard title="Fixed Costs" value={fixedCostSummary[0].value} backgroundImage={'linear-gradient(to right, rgb(250, 250, 250), rgb(232, 232, 232))'} borderColor={'rgb(233, 213, 255)'} subTitle={fixedCostSummary[1].title + ': '+ fixedCostSummary[1].value} icon={<MoveRight color='rgb(168, 85, 247)' />} />
      </YStack> */}
      <TZASummaryCards 
        cards={[
          { title: "Monthly Spending", value:budget?.financialSnapshotUI?.SpendingPieChart?.Spending?.fmt, subTitle: '‎ ', icon: <DollarSign color='rgb(16, 185, 129)'/> },
          { title: "Variable Costs", value: variableCostSummary[0].value, subTitle: variableCostSummary[1].title + ': '+ variableCostSummary[1].value, icon: <TrendingUp color='rgb(59, 130, 246)' /> },
          { title: "Fixed Costs", value: fixedCostSummary[0].value, subTitle: fixedCostSummary[1].title + ': '+ fixedCostSummary[1].value, icon: <MoveRight color='rgb(168, 85, 247)' /> }
        ]}
      />
      <Tabs
        defaultValue="tab1"
        orientation="horizontal"
        flexDirection="column"
        //$maxMd={{ width: 300 }}
        width={'100%'}
        //style={{flexGrow: 1}}
        //height={150}
        borderRadius="$4"
        borderWidth="$0.25"
        overflow="hidden"
        borderColor="rgb(240,240,240)"
      >
        <TZATabs.List
          separator={<Separator vertical />}
          //disablePassBorderRadius="bottom"
          aria-label="Manage your account"
        >
          <TZATabs.Tab
            value="tab1"
          >
            <SizableText fontFamily="$body" textAlign="center">
              Overview
            </SizableText>
          </TZATabs.Tab>
          <TZATabs.Tab
            value="tab2"
            style={{display: 'flex', flexWrap: 'wrap'}}
          >
            
            <SizableText lineHeight={14} fontFamily="$body" textAlign="center">
              Variable{' '}
            </SizableText>
            <SizableText lineHeight={14} fontFamily="$body" textAlign="center">
              Costs
            </SizableText>
          </TZATabs.Tab>
          <TZATabs.Tab
            value="tab3"
            style={{display: 'flex', flexWrap: 'wrap'}}
          >
            <SizableText lineHeight={14} fontFamily="$body" textAlign="center">
              Fixed{' '}
            </SizableText>
            <SizableText lineHeight={14} fontFamily="$body" textAlign="center">
              Costs
            </SizableText>
          </TZATabs.Tab>
        </TZATabs.List>
        <Separator />
        <TabsContent value="tab1">
          {/* <H5>Profile</H5> */}
          {/* Income and Spending Breakdown Charts  */}
          {budget && <SpendingChart monthlySpendingAlalysisData={[]} monthlyCashFlows={budget?.['monthlyCashFlows']} />}
        </TabsContent>

        <TabsContent value="tab2">
          {/* <H5>Connections</H5> */}
          <ExpenseCategoryList data={variableCostData} />
        </TabsContent>

        <TabsContent value="tab3">
          {/* <H5>Notifications</H5> */}
          <ExpenseCategoryList data={fixedCostData} />
        </TabsContent>
      </Tabs>

      {/* Variable Costs Section */}
      {/* <BudgetSummary header="Variable Costs" listData={variableCostSummary} /> */}

      {/* Period Toggle */}
      {/* <PeriodToggle active={activePeriod} setActive={setActivePeriod} /> */}

      {/* Expense Category List for Variable Costs */}
      

      {/* Fixed Costs Section */}
      {/* <BudgetSummary header="Fixed Costs" listData={fixedCostSummary} /> */}
      {/* Period Toggle */}
      {/* <PeriodToggle active={activePeriod} setActive={setActivePeriod} /> */}

      {/* Expense Category List for Fixed Costs */}
      

      {/* Budget Breakdown Table */}
      <BudgetBreakdownTable data={budgetBreakdownData} filter={filter} setFilter={setFilter} />
  </>
  const output = (
    <YStack p="$4" bg="#fff" gap={25}>
      <FinancialReviewHeading
        title="Budget Analysis"
        img={images.spendingAnalysis}
        //imageStyle={getImageStyle()}
      />

      {body}

    </YStack>
  );

  return (
    Platform.OS === 'web' ? (
      output
    ) : (
      <ScrollView backgroundColor={color.white}>
        {output}
      </ScrollView>
    )
  );
}

// Helper for image style
const getImageStyle = () => ({
  '$platform-web': { height: 27, width: 27 },
  '$platform-native': { height: 20, width: 20 },
})

// Format Budget Breakdown for Financial Table
const formatBudgetBreakdown = (budgetBreakdown: any, needs: any[], wants: any[]) => {
  return Object.keys(budgetBreakdown).map((item) => ({
    expander: {
      expander: {
        renderFn: ({ onClick, isExpanded }) => {
          return (item === 'Savings') ? null : <XStack alignItems='center'><Button
            {...{
              //onClick,
              onPress: onClick,
              style: { cursor: 'pointer', marginLeft: 10 },
            }}
            $sm={{size: '$2'}}
            //icon={isExpanded ? Minus : Plus}
          >
                            <ChevronDown size="$1" transition="quick" rotate={isExpanded ? '180deg' : '0deg'}/>
                          </Button></XStack>
        }
      }
    },
    category: item,
    percentageOfSalary: FormatUtils.numeric.percentage(parseFloat(budgetBreakdown[item]['% of Monthly Salary']).toFixed(2)),
    amountSpent: FormatUtils.currency.cashFormat(parseFloat(budgetBreakdown[item].amountSpent)),
    target: FormatUtils.numeric.percentage(parseFloat(budgetBreakdown[item].target)),
    targetAmount: FormatUtils.currency.cashFormat(parseFloat(budgetBreakdown[item].targetAmount)),
    difference: FormatUtils.currency.cashFormat(parseFloat(budgetBreakdown[item].difference)),
    individualCosts: item === 'Needs' 
      ? needs 
      : item === 'Wants' 
        ? wants 
        : []
  }))
}

// Period Toggle Component
const PeriodToggle = ({ active, setActive }: { active: string; setActive: Function }) => (
  <XStack justifyContent="space-between" flexWrap="wrap" alignItems="center">
    <SimpleText fontWeight="800" color={color.lightGrey} fontSize={14}>
      Category
    </SimpleText>
    <ToggleButtonGroup
      active={active}
      onChange={setActive}
      options={['1 Month', '3 Months', '6 Months']}
    />
  </XStack>
)

// Filter Tabs Component
const FilterTabs = ({ filter, setFilter }) => {
  const { cost, priorities } = filter
  const { width } = useWindowDimensions()
  const tabs = ['All', 'Fixed Cost', 'Variable Cost']

  return (
    <XStack alignItems="center" gap={8} flexWrap="wrap" maxWidth={width < 400 ? 300 : 500}>
      {tabs.map((tab) => (
        <ActionButton
          key={tab}
          onPress={() => setFilter((previousState) => ({ ...previousState, cost: tab }))}
          style={{
            borderWidth: 1,
            borderColor: cost === tab ? 'transparent' : color.tzaGreen,
            borderRadius: 100,
            backgroundColor: cost === tab ? color.tzaGreen : 'transparent',
            height: 35,
            minWidth: 50,
          }}
          pressStyle={{
            opacity: 0.8,
          }}
        >
          <SimpleText
            style={{
              color: cost === tab ? color.white : color.tzaGreen,
              fontSize: 13,
              fontWeight: cost === tab ? '600' : 'normal',
            }}
          >
            {tab}
          </SimpleText>
        </ActionButton>
      ))}
      <YStack>
        <CustomDropdown
          items={DEFAULT_GOAL_ITEMS}
          val={priorities}
          setVal={(val) => setFilter((previousState) => ({ ...previousState, priorities: val }))}
          placeholder={'Priorities'}
        />
      </YStack>
    </XStack>
  )
}

// Budget Breakdown Table Component
const BudgetBreakdownTable = ({
  data,
  setFilter,
  filter,
}: {
  data: any
  setFilter: any
  filter: any
}) => (
  <YStack gap={10}>
    <XStack flexWrap="wrap" gap={15} alignItems={'center'} justifyContent="space-between">
      <SimpleText
        color={color.darkNavy}
        style={{ fontSize: 15, lineHeight: 15, fontWeight: '800' }}
      >
        Budget Breakdown
      </SimpleText>
      {/* <FilterTabs setFilter={setFilter} filter={filter} /> */}
    </XStack>
    <FinancialTable 
      data={data} 
      headings={DEFAULT_TABLE_HEADINGS} 
      columnWidth={120}  
      expandProps={{
        getRowCanExpand: (row) => { return true},
        renderSubComponent: (row, idx) => {
          const individualCosts = row.original['individualCosts'];
          return <YStack paddingVertical={10} key={idx}><ExpenseCategoryList data={individualCosts} collapsible leftBorder={false} /></YStack>
        },
        forceExpanded: true
      }}
      mobileRenderOptions={{
        titleKey: 'category',
        //mainValueKeys: ['status', 'action'],
        interactiveKeys: [/* 'expander' */],
        otherKeys: [
          ["percentageOfSalary", 'target'],
          ["amountSpent", 'targetAmount'],
          ["difference"],
        ]
      }}
    />
  </YStack>
)

// Constants for dropdown items
const DEFAULT_GOAL_ITEMS = [
  { label: 'All', value: 'All' },
  { label: 'Wants', value: 'Wants' },
  { label: 'Needs', value: 'Needs' },
  { label: 'Savings', value: 'Savings' },
]
